import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { buildEnv } from 'build-environment';
import { normalizeWordGuessWord } from '../logic/word-guess-alphabet';

const __BUILD_VERSION__: string = buildEnv.buildVersion;

/*
 * Holds the list of words a player is allowed to guess.
 *
 * The source is a plain text file, one uppercase word per line, configured through
 * `wordGuessDictionaryUrl`. It is a flat list of every supported length; the service
 * buckets it by word length on load, so a 6-letter puzzle can use the same file.
 *
 * The URL is the extension point for the announced backend: pointing the config key at an
 * endpoint that answers with the same line-based format is all it takes.
 */
@Injectable({
  providedIn: 'root',
})
export class WordGuessDictionaryService {
  private readonly loaded: { [url: string]: Promise<{ [wordLength: number]: Set<string> }> } = {};

  private wordsByLength: { [wordLength: number]: Set<string> } = {};
  private available = false;

  constructor(private http: HttpClient) {}

  /*
   * Loads and caches the dictionary. A failing or empty file is not fatal: the board falls
   * back to accepting every well-formed guess rather than locking the player out.
   */
  public async load(url: string): Promise<boolean> {
    if (!url) {
      this.available = false;
      return false;
    }

    if (!this.loaded[url]) {
      this.loaded[url] = this.fetch(url);
    }

    this.wordsByLength = await this.loaded[url];
    this.available = Object.keys(this.wordsByLength).length > 0;

    return this.available;
  }

  /*
   * True when a dictionary is actually loaded for this word length. Callers use it to skip
   * validation instead of rejecting everything.
   */
  public canValidate(wordLength: number): boolean {
    return this.available && !!this.wordsByLength[wordLength] && this.wordsByLength[wordLength].size > 0;
  }

  public has(word: string, wordLength: number): boolean {
    if (!this.canValidate(wordLength)) {
      return true;
    }

    return this.wordsByLength[wordLength].has(normalizeWordGuessWord(word));
  }

  private async fetch(url: string): Promise<{ [wordLength: number]: Set<string> }> {
    const wordsByLength: { [wordLength: number]: Set<string> } = {};

    try {
      const data = await firstValueFrom(
        this.http.get(url, {
          responseType: 'text',
          params: {
            version: __BUILD_VERSION__,
          },
        })
      );

      data.split(/\r?\n/).forEach((line) => {
        const word = normalizeWordGuessWord(line.trim());

        if (!word.length) {
          return;
        }

        if (!wordsByLength[word.length]) {
          wordsByLength[word.length] = new Set<string>();
        }

        wordsByLength[word.length].add(word);
      });
    } catch (error) {
      console.warn(`Word Guess dictionary could not be loaded from '${url}', guesses will not be validated.`, error);
    }

    return wordsByLength;
  }
}
